export const SHOPIFY_API_VERSION = "2025-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "suomi-soft-play-epz24.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = "43f75a5c99b9da115cd5ce0c9f69bcc7";

/** Shopify Storefront LanguageCode for a given UI locale. */
export type ShopifyLang = "FI" | "EN" | "SV" | "ET";
export type ShopifyCountry = "FI" | "US";

/**
 * Module-level context injected into every Storefront API call so the same
 * queries work for FI (EUR) or US (USD). Updated from a React effect on
 * route change; SSR always renders FI/FI, then client re-fetches with the
 * active market.
 */
let CURRENT_COUNTRY: ShopifyCountry = "FI";
// The Shopify primary locale (EN slot) holds our curated, hand-written
// Finnish product copy. The FI "translation" locale still contains the
// old supplier text (partly English, partly machine Finnish), so asking
// for language: FI would downgrade titles, descriptions and therefore
// every SEO title / meta description. Always read the primary locale
// until the FI translation layer is cleaned up in Shopify.
let CURRENT_LANGUAGE: ShopifyLang = "EN";

export function setShopifyContext(ctx: { country: ShopifyCountry; language: ShopifyLang }) {
  CURRENT_COUNTRY = ctx.country;
  CURRENT_LANGUAGE = ctx.language;
}
export function getShopifyContext() {
  return { country: CURRENT_COUNTRY, language: CURRENT_LANGUAGE };
}

/** Currency defaults per market — used before Shopify responds with prices. */
export function currencyForCountry(country: ShopifyCountry): "EUR" | "USD" {
  return country === "US" ? "USD" : "EUR";
}

export function langForLocale(locale: string | undefined | null): ShopifyLang {
  switch (locale) {
    case "sv": return "SV";
    // fi / en / et all resolve to the primary locale, which contains the
    // curated Finnish catalogue copy (see CURRENT_LANGUAGE above).
    default: return "EN";
  }
}

/**
 * Append width to a Shopify CDN image URL. Shopify's CDN negotiates WebP
 * automatically via the Accept header, so we do not force format=webp.
 * Other URLs are returned unchanged.
 */
export function shopifyImg(url: string, width: number): string {
  if (!url || !url.includes("cdn.shopify.com")) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("width", String(width));
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Build a responsive srcset with w-descriptors so the browser can pick the
 * right file for the current viewport / DPR based on the `sizes` attribute.
 * The `base` arg is kept for backwards compatibility but ignored — the set
 * spans small phones to large desktops.
 */
export function shopifySrcSet(url: string, _base?: number): string {
  if (!url || !url.includes("cdn.shopify.com")) return "";
  // Card/rail-focused ladder. Cards render up to ~500px CSS; 2–3× DPR caps
  // the useful width around 1000w. Fewer stops = fewer distinct CDN objects
  // to cache and smaller `srcset` HTML payload.
  // The 600w stop matters: on typical 360–414px phones (DPR ~1.75–2) a
  // ~280–325px card needs 490–650 device px, so without it the browser
  // jumps straight from 480w to 720w (~40% heavier than needed).
  const widths = [240, 360, 480, 600, 720, 960];
  return widths.map((w) => `${shopifyImg(url, w)} ${w}w`).join(", ");
}

/**
 * Wider ladder for large hero-style images (PDP main image, lightbox).
 */
export function shopifySrcSetLarge(url: string): string {
  if (!url || !url.includes("cdn.shopify.com")) return "";
  const widths = [480, 720, 960, 1280, 1600];
  return widths.map((w) => `${shopifyImg(url, w)} ${w}w`).join(", ");
}

/**
 * Alt text for a product image.
 *
 * Part of the catalogue still carries supplier-provided English alt text
 * ("Soft Play Ball Pit - White"). Customer-facing copy must be Finnish, so any
 * alt that reads as English is dropped in favour of the (Finnish) product
 * title. Genuine Finnish alt text from the merchant is kept as-is.
 */
const ENGLISH_ALT =
  /\b(with|without|and|for|the|set|soft play|wooden|kids?|baby|toddlers?|natural|white|green|blue|pink|red|grey|gray|honey|pastel|turquoise|salmon|chair|table|shelf|rack|toy|climber|tower|stool|block|balls?|mat|ramp|wave|bridge|slide|tunnel|wedge|cube|explorer|adventurer|discoverer|crawler|climber)\b/i;

export function productImageAlt(
  altText: string | null | undefined,
  title: string,
): string {
  const alt = (altText ?? "").trim();
  if (!alt || ENGLISH_ALT.test(alt)) return title;
  return alt;
}

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description?: string;
    descriptionHtml?: string;
    handle: string;
    vendor?: string;
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
    images: { edges: Array<{ node: { url: string; altText: string | null } }> };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: { amount: string; currencyCode: string };
          availableForSale: boolean;
          selectedOptions: Array<{ name: string; value: string }>;
        };
      }>;
    };
    options?: Array<{ name: string; values: string[] }>;
  };
}

export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    products(first: $first, query: $query) {
      edges {
        node {
          id title handle vendor
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 1) { edges { node { url altText } } }
          variants(first: 1) {
            edges {
              node {
                id title
                price { amount currencyCode }
                availableForSale
                selectedOptions { name value }
              }
            }
          }
        }
      }
    }
  }
`;
// Back-compat alias for the old function-style export.
export const productsQuery = (_lang?: ShopifyLang) => PRODUCTS_QUERY;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProduct($handle: String!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      id title description descriptionHtml handle vendor productType tags
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 10) { edges { node { url altText } } }
      media(first: 10) {
        edges {
          node {
            mediaContentType
            ... on Video {
              sources { url mimeType format width height }
              previewImage { url altText }
            }
          }
        }
      }
      variants(first: 25) {
        edges {
          node {
            id title sku
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
      options { name values }
      metafields(identifiers: [
        {namespace: "custom", key: "material"},
        {namespace: "custom", key: "materiaali"},
        {namespace: "custom", key: "age"},
        {namespace: "custom", key: "age_range"},
        {namespace: "custom", key: "ikasuositus"},
        {namespace: "custom", key: "ika"},
        {namespace: "custom", key: "dimensions"},
        {namespace: "custom", key: "mitat"},
        {namespace: "custom", key: "weight"},
        {namespace: "custom", key: "paino"},
        {namespace: "custom", key: "color"},
        {namespace: "custom", key: "vari"},
        {namespace: "custom", key: "care"},
        {namespace: "custom", key: "hoito"},
        {namespace: "custom", key: "certifications"},
        {namespace: "custom", key: "sertifikaatit"},
        {namespace: "custom", key: "origin"},
        {namespace: "custom", key: "alkupera"},
        {namespace: "custom", key: "package_contents"},
        {namespace: "custom", key: "pakkauksen_sisalto"},
        {namespace: "custom", key: "highlights"},
        {namespace: "custom", key: "kohokohdat"},
        {namespace: "custom", key: "features"},
        {namespace: "custom", key: "ominaisuudet"},
        {namespace: "custom", key: "safety"},
        {namespace: "custom", key: "turvallisuus"},
        {namespace: "custom", key: "warranty"},
        {namespace: "custom", key: "takuu"},
        {namespace: "custom", key: "assembly"},
        {namespace: "custom", key: "kokoaminen"},
        {namespace: "custom", key: "related"},
        {namespace: "specs", key: "material"},
        {namespace: "specs", key: "dimensions"},
        {namespace: "specs", key: "weight"},
        {namespace: "specs", key: "age"},
        {namespace: "descriptors", key: "care_guide"},
        {namespace: "descriptors", key: "subtitle"},
        {namespace: "shopify", key: "age-group"},
        {namespace: "shopify", key: "color-pattern"},
        {namespace: "shopify", key: "material"},
        {namespace: "shopify", key: "target-gender"}
      ]) {
        key
        namespace
        type
        value
        reference {
          ... on MediaImage { image { url altText } }
          ... on Metaobject {
            id type handle
            fields { key type value reference { ... on MediaImage { image { url altText } } } }
          }
        }
        references(first: 25) {
          edges {
            node {
              ... on MediaImage { image { url altText } }
              ... on Metaobject {
                id type handle
                fields { key type value reference { ... on MediaImage { image { url altText } } } }
              }
            }
          }
        }
      }
    }
  }
`;
export const productByHandleQuery = (_lang?: ShopifyLang) => PRODUCT_BY_HANDLE_QUERY;

export const CART_QUERY = `query cart($id: ID!) { cart(id: $id) { id totalQuantity } }`;

export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    cartCreate(input: $input) {
      cart {
        id checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id } userErrors { field message } }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id } userErrors { field message } }
  }
`;

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  // Auto-inject the current market context (country + language) unless the
  // caller already provided them explicitly. This keeps every existing call
  // site working while letting the switcher change context globally.
  const merged: Record<string, unknown> = {
    country: CURRENT_COUNTRY,
    language: CURRENT_LANGUAGE,
    ...variables,
  };
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables: merged }),
  });

  if (response.status === 402) {
    if (typeof window !== "undefined") {
      const { toast } = await import("sonner");
      toast.error("Shopify: Maksu vaaditaan", {
        description: "Shopify-tilauksesi tarvitsee aktiivisen tilauksen. Käy osoitteessa https://admin.shopify.com.",
      });
    }
    return;
  }

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (data.errors) throw new Error(`Shopify error: ${data.errors.map((e: { message: string }) => e.message).join(", ")}`);
  return data;
}

export function formatCheckoutUrl(
  checkoutUrl: string,
  locale: string = "fi",
  country: ShopifyCountry = "FI",
): string {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set("channel", "online_store");
    url.searchParams.set("locale", locale);
    url.searchParams.set("country", country);
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

export function formatMoney(amount: string | number, currencyCode = "EUR") {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  const hasFraction = Math.round(n * 100) % 100 !== 0;
  // Use en-US formatting when we're showing USD so the number reads
  // naturally to US shoppers ($49.90 not 49,90 $).
  const isUsd = currencyCode === "USD";
  return new Intl.NumberFormat(isUsd ? "en-US" : "fi-FI", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}
